import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Project } from '../../types/project.js';
import * as projectApi from '../../services/project-api.js';
import * as boardApi from '../../services/board-api.js';
import { getCaretCoordinates, extractMentionContext } from '../../utils/textarea-caret.js';
import type { CpFileMentionDropdown } from '../shared/cp-file-mention-dropdown.js';
import '../shared/cp-skill-selector.js';

const PRIORITY_OPTIONS = ['critical', 'high', 'medium', 'low'];
const CATEGORY_OPTIONS: { value: 'todo' | 'bug'; label: string }[] = [
  { value: 'todo', label: 'Todo' },
  { value: 'bug', label: 'Bug' },
];

@customElement('cp-board-create-modal')
export class CpBoardCreateModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;

  @state() private _title = '';
  @state() private _description = '';
  @state() private _priority = 'medium';
  @state() private _category: 'todo' | 'bug' = 'todo';
  @state() private _projectId = 0;
  @state() private _projects: Project[] = [];
  @state() private _loadingProjects = false;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _titleError = '';
  @state() private _projectError = '';
  @state() private _skillIds: string[] = [];
  @state() private _mentionOpen = false;
  @state() private _mentionQuery = '';
  @state() private _mentionAnchor = { top: 0, left: 0 };
  @state() private _confirmClose = false;

  private _confirmTimer = 0;

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      this._resetForm();
      this._loadProjects();
    }
  }

  private _resetForm(): void {
    this._title = '';
    this._description = '';
    this._priority = 'medium';
    this._category = 'todo';
    this._projectId = 0;
    this._skillIds = [];
    this._saving = false;
    this._error = '';
    this._titleError = '';
    this._projectError = '';
    this._mentionOpen = false;
    this._mentionQuery = '';
    this._confirmClose = false;
    window.clearTimeout(this._confirmTimer);
  }

  private async _loadProjects(): Promise<void> {
    this._loadingProjects = true;
    try {
      this._projects = await projectApi.getProjects('active');
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Projekte konnten nicht geladen werden';
    } finally {
      this._loadingProjects = false;
    }
  }

  private _close(): void {
    window.clearTimeout(this._confirmTimer);
    this._confirmClose = false;
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
  }

  private get _hasContent(): boolean {
    return !!(this._title.trim() || this._description.trim());
  }

  private _requestClose(): void {
    if (!this._hasContent || this._confirmClose) {
      this._close();
      return;
    }
    this._confirmClose = true;
    window.clearTimeout(this._confirmTimer);
    this._confirmTimer = window.setTimeout(() => { this._confirmClose = false; }, 3000);
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this._requestClose();
    }
  };

  private _onBackdropClick(e: Event): void {
    if (e.target === e.currentTarget) {
      this._requestClose();
    }
  }

  private _onTitleInput(e: Event): void {
    this._title = (e.target as HTMLInputElement).value;
  }

  private _onTitleBlur(): void {
    this._titleError = !this._title.trim() ? 'Titel ist erforderlich' : '';
  }

  private _onDescriptionInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this._description = textarea.value;
    this._updateMention(textarea);
  }

  private _updateMention(textarea: HTMLTextAreaElement): void {
    if (!this._projectId) {
      this._mentionOpen = false;
      return;
    }
    const ctx = extractMentionContext(textarea);
    if (ctx) {
      this._mentionQuery = ctx.query;
      this._mentionAnchor = getCaretCoordinates(textarea);
      this._mentionOpen = true;
    } else {
      this._mentionOpen = false;
    }
  }

  private _onDescriptionKeydown(e: KeyboardEvent): void {
    if (this._mentionOpen) {
      const dropdown = this.querySelector('cp-file-mention-dropdown') as CpFileMentionDropdown | null;
      if (dropdown?.handleKeydown(e)) return;
    }
  }

  private _onFileSelected(e: CustomEvent<{ path: string }>): void {
    const textarea = this.querySelector('.cp-board-create-modal__textarea') as HTMLTextAreaElement | null;
    if (!textarea) return;

    const ctx = extractMentionContext(textarea);
    if (!ctx) return;

    const before = this._description.substring(0, ctx.startIndex);
    const after = this._description.substring(textarea.selectionEnd);
    this._description = `${before}@${e.detail.path} ${after}`;
    this._mentionOpen = false;

    this.updateComplete.then(() => {
      if (textarea) {
        const newPos = ctx.startIndex + e.detail.path.length + 2;
        textarea.value = this._description;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      }
    });
  }

  private _onMentionDismissed(): void {
    this._mentionOpen = false;
  }

  private _onPriorityChange(e: Event): void {
    this._priority = (e.target as HTMLSelectElement).value;
  }

  private _onCategoryChange(e: Event): void {
    this._category = (e.target as HTMLSelectElement).value as 'todo' | 'bug';
  }

  private _onSkillsChanged(e: CustomEvent<{ skillIds: string[] }>): void {
    this._skillIds = e.detail.skillIds;
  }

  private _onProjectChange(e: Event): void {
    this._projectId = Number((e.target as HTMLSelectElement).value);
    this._projectError = '';
    this._skillIds = [];
  }

  private async _onSave(): Promise<void> {
    if (this._saving) return;

    const trimmedTitle = this._title.trim();
    let hasError = false;

    if (!trimmedTitle) {
      this._titleError = 'Titel ist erforderlich';
      hasError = true;
    }
    if (!this._projectId) {
      this._projectError = 'Projekt ist erforderlich';
      hasError = true;
    }
    if (hasError) return;

    this._saving = true;
    this._error = '';
    this._titleError = '';
    this._projectError = '';

    try {
      await boardApi.createBacklogItem({
        title: trimmedTitle,
        description: this._description.trim() || undefined,
        priority: this._priority,
        category: this._category,
        projectId: this._projectId,
        skillIds: this._skillIds.length > 0 ? this._skillIds : undefined,
      });

      this.dispatchEvent(new CustomEvent('backlog-item-created', {
        bubbles: true,
        composed: true,
      }));

      this._close();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Backlog-Item konnte nicht erstellt werden';
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <div class="cp-board-create-modal__overlay" @click=${this._onBackdropClick}>
        <div class="cp-board-create-modal__dialog">
          <div class="cp-board-create-modal__header">
            <h2 class="cp-board-create-modal__heading">Backlog-Item erstellen</h2>
            <button class="cp-board-create-modal__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-board-create-modal__body">
            <div class="cp-board-create-modal__field">
              <label class="cp-board-create-modal__label">Titel *</label>
              <input
                class="cp-board-create-modal__input ${this._titleError ? 'cp-board-create-modal__input--error' : ''}"
                type="text"
                .value=${this._title}
                @input=${this._onTitleInput}
                @blur=${this._onTitleBlur}
                placeholder="Titel eingeben"
              />
              ${this._titleError ? html`
                <span class="cp-board-create-modal__field-error">${this._titleError}</span>
              ` : nothing}
            </div>

            <div class="cp-board-create-modal__field">
              <label class="cp-board-create-modal__label">Beschreibung</label>
              <textarea
                class="cp-board-create-modal__textarea"
                rows="6"
                .value=${this._description}
                @input=${this._onDescriptionInput}
                @keydown=${this._onDescriptionKeydown}
                placeholder="Optionale Beschreibung"
              ></textarea>
              <cp-file-mention-dropdown
                .projectId=${this._projectId}
                .open=${this._mentionOpen}
                .query=${this._mentionQuery}
                .anchorRect=${this._mentionAnchor}
                @file-selected=${this._onFileSelected}
                @mention-dismissed=${this._onMentionDismissed}
              ></cp-file-mention-dropdown>
            </div>

            <div class="cp-board-create-modal__row">
              <div class="cp-board-create-modal__field cp-board-create-modal__field--half">
                <label class="cp-board-create-modal__label">Prioritaet</label>
                <select
                  class="cp-board-create-modal__select"
                  .value=${this._priority}
                  @change=${this._onPriorityChange}
                >
                  ${PRIORITY_OPTIONS.map(p => html`
                    <option value=${p} ?selected=${this._priority === p}>${p}</option>
                  `)}
                </select>
              </div>

              <div class="cp-board-create-modal__field cp-board-create-modal__field--half">
                <label class="cp-board-create-modal__label">Kategorie</label>
                <select
                  class="cp-board-create-modal__select"
                  .value=${this._category}
                  @change=${this._onCategoryChange}
                >
                  ${CATEGORY_OPTIONS.map(c => html`
                    <option value=${c.value} ?selected=${this._category === c.value}>${c.label}</option>
                  `)}
                </select>
              </div>
            </div>

            <div class="cp-board-create-modal__field">
              <label class="cp-board-create-modal__label">Projekt *</label>
              <select
                class="cp-board-create-modal__select ${this._projectError ? 'cp-board-create-modal__select--error' : ''}"
                @change=${this._onProjectChange}
                ?disabled=${this._loadingProjects}
              >
                <option value="0" ?selected=${this._projectId === 0}>
                  ${this._loadingProjects ? 'Lade Projekte...' : '-- Projekt waehlen --'}
                </option>
                ${this._projects.map(p => html`
                  <option value=${p.id} ?selected=${this._projectId === p.id}>${p.name}</option>
                `)}
              </select>
              ${this._projectError ? html`
                <span class="cp-board-create-modal__field-error">${this._projectError}</span>
              ` : nothing}
            </div>

            <div class="cp-board-create-modal__field">
              <label class="cp-board-create-modal__label">Skill</label>
              <cp-skill-selector .selectedIds=${this._skillIds} .projectId=${this._projectId || null} @skills-changed=${this._onSkillsChanged}></cp-skill-selector>
            </div>

            ${this._error ? html`
              <div class="cp-board-create-modal__error">${this._error}</div>
            ` : nothing}
          </div>

          ${this._confirmClose ? html`
            <div class="cp-board-create-modal__confirm-close">
              Eingaben verwerfen? Nochmal ESC oder Klick zum Schliessen.
            </div>
          ` : nothing}

          <div class="cp-board-create-modal__footer">
            <button
              class="cp-board-create-modal__btn cp-board-create-modal__btn--cancel"
              @click=${this._requestClose}
            >Abbrechen</button>
            <button
              class="cp-board-create-modal__btn cp-board-create-modal__btn--save"
              ?disabled=${this._saving}
              @click=${this._onSave}
            >${this._saving ? 'Erstellen...' : 'Erstellen'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-board-create-modal': CpBoardCreateModal;
  }
}
